import type { components } from "@/api/schema";
import type { IntentName } from "@/design";

export type NotificationEvent = components["schemas"]["NotificationEvent"];
export type NotificationTarget = components["schemas"]["NotificationTarget"];

export interface NotifFilter {
  severity?: "P1" | "P2" | "P3";
  type?: string;
  read?: boolean;
  offset?: number;
  limit?: number;
}

export const SEVERITY_INTENT: Record<"P1" | "P2" | "P3", IntentName> = {
  P1: "danger",
  P2: "warning",
  P3: "neutral",
};

// The complete, fixed set of event types this system emits — mirrors the
// backend's notifications.DefaultSettings() routing matrix (settings.go),
// which the code comments there call "the final 6-type matrix". Deliberately
// NOT derived from loaded rows: a type with zero rows on the current page
// must still appear as a filter option.
export const NOTIFICATION_TYPES = [
  "sync",
  "oversold",
  "quota",
  "quota_low",
  "diff",
  "order_oos",
  "sync_stale",
  "sync_paused",
  "buyer_cancel_requested",
  "listing_discovery",
  "listing_multi_warehouse",
] as const;

export function targetToRoute(target: NotificationTarget | null | undefined): string | null {
  if (!target) return null;

  switch (target.view) {
    case "diff":
      return `/inventory-diff?highlight=${encodeURIComponent(target.sku)}`;
    case "inbound":
      return `/inbound-report?highlight=${encodeURIComponent(target.sku)}`;
    case "inventory":
      return `/inventory?highlight=${encodeURIComponent(target.sku)}`;
    case "order":
      // Exception notifications (internal/order/wire/exceptions.go) carry the
      // order in `order_id`; the older OrderOOS() alert repurposed `sku`.
      return `/orders/${encodeURIComponent(target.order_id || target.sku)}`;
    case "settings/marketplace-connections":
      // listing_discovery / listing_multi_warehouse target this view — see
      // internal/listingdiscovery/wire/notifalerter.go in atlas-oms-backend.
      return "/settings/marketplace-connections";
    default:
      return null;
  }
}

// Typed navigate() options per target view, computed directly from the
// target — not by re-parsing the string targetToRoute() builds. That
// round-trip (via the now-removed splitCtaRoute/CtaRoutePath) was the bug:
// it silently dropped the highlight/search params on every CTA click.
export type CtaNavigation =
  | { to: "/inventory-diff"; search: { highlight: string } }
  | { to: "/inbound-report"; search: { highlight: string } }
  | { to: "/inventory"; search: { search: string; offset: number } }
  | { to: "/orders/$saleOrderId"; params: { saleOrderId: string } }
  | { to: "/settings/marketplace-connections" };

export function targetToNavigation(
  target: NotificationTarget | null | undefined,
): CtaNavigation | null {
  if (!target) return null;

  switch (target.view) {
    case "diff":
      return { to: "/inventory-diff", search: { highlight: target.sku } };
    case "inbound":
      return { to: "/inbound-report", search: { highlight: target.sku } };
    case "inventory":
      return { to: "/inventory", search: { search: target.sku, offset: 0 } };
    case "order":
      return { to: "/orders/$saleOrderId", params: { saleOrderId: target.order_id || target.sku } };
    case "settings/marketplace-connections":
      return { to: "/settings/marketplace-connections" };
    default:
      return null;
  }
}

/** Format a date string as a human-relative label.
 *  - < 60 s  → "just now"
 *  - < 60 m  → "Xm ago"
 *  - < 24 h  → "Xh ago"
 *  - < 30 d  → "Xd ago"
 *  - ≥ 30 d  → absolute date (toLocaleDateString)
 *  - future  → "just now"
 */
export function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;

  // Future timestamps — treat as "just now"
  if (diffMs < 0) return "just now";

  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  // Fallback to absolute date for deltas beyond ~30 days
  return new Date(dateStr).toLocaleDateString();
}
